import type { Chart } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { INDICATOR_DEFAULT_COLORS, type IndicatorDef } from '@/components/Chart/indicatorDefs';
import { ensureBreadthData } from '@/store/breadthStore';

type ChartV9 = Chart & {
  createIndicator: (
    name: string,
    isStack: boolean,
    paneOptions?: { id?: string; height?: number; minHeight?: number }
  ) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
};

/**
 * Indicators whose add/remove needs extra store flags or paired panes — IndicatorBar's toggle
 * handles them in dedicated branches, so the generic helpers below must not be used for them.
 */
export const SPECIAL_TOGGLE_INDICATORS = new Set([
  'RS_LINE', 'EPS_LINE', 'CUP_HANDLE', 'WILLIAM_ONEIL_SELL', 'MOMENTUM_OVERLAY', 'MOMENTUM_TRACKER',
]);

/**
 * Create a standard indicator with the app defaults from indicatorDefs.ts and record it in the
 * store. Returns false when it couldn't be added (breadth data fetch failed).
 */
export async function addStandardIndicator(chart: Chart, ind: IndicatorDef): Promise<boolean> {
  const c = chart as ChartV9;
  if (ind.requiresBreadthData) {
    const ok = await ensureBreadthData();
    if (!ok) return false; // don't create indicator if data fetch failed
  }
  const opts = ind.paneId
    ? { id: ind.paneId, height: 80, minHeight: 60 }
    : { height: ind.paneHeight ?? 100, minHeight: 60 };
  const returnedPaneId = c.createIndicator(ind.name, !!ind.paneId, opts);
  const resolvedId = ind.paneId ?? returnedPaneId ?? ind.name;
  // When re-adding VOL, also re-create its companion stacked indicators.
  if (ind.name === 'VOL') {
    try { c.createIndicator('VOL_LABELS', true, { id: 'vol_pane' }); } catch { /* ignore */ }
    try { c.createIndicator('CROSS_VOL', true, { id: 'vol_pane' }); } catch { /* ignore */ }
  }
  if (ind.name === 'RSI') {
    try { c.createIndicator('RSI_ZONES', true, { id: resolvedId }); } catch { /* ignore */ }
    try {
      c.createIndicator('RSI_DIVERGENCE', true, { id: resolvedId });
      c.overrideIndicator({
        name: 'RSI_DIVERGENCE',
        calcParams: [
          useChartStore.getState().rsiDivergencePeriod,
          useChartStore.getState().rsiDivergencePivotLookback,
        ],
      }, resolvedId);
    } catch { /* ignore */ }
  }

  // Apply app defaults so the chart state matches indicatorDefs.ts
  const store = useChartStore.getState();
  const defaultColors = INDICATOR_DEFAULT_COLORS[ind.name] ?? [];
  if ((ind.defaultParams?.length ?? 0) > 0 || defaultColors.length > 0) {
    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (ind.defaultParams?.length) override.calcParams = ind.defaultParams;
    if (defaultColors.length > 0) {
      if (ind.name === 'VOL') {
        const maColors = defaultColors.slice(2);
        override.styles = {
          bars: [{
            style: 'fill',
            upColor: defaultColors[0],
            downColor: defaultColors[1] ?? defaultColors[0],
            noChangeColor: defaultColors[0],
          }],
          ...(maColors.length ? { lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
        };
      } else {
        override.styles = { lines: defaultColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) };
      }
    }
    c.overrideIndicator(override, resolvedId);
  }
  if (ind.defaultParams?.length) store.setIndicatorParam(ind.name, ind.defaultParams);
  if (defaultColors.length > 0) store.setIndicatorColors(ind.name, defaultColors);

  store.setIndicatorPaneMap(new Map([...useChartStore.getState().indicatorPaneMap, [ind.name, resolvedId]]));
  void useChartStore.getState().rebalancePanes();
  return true;
}

/** Remove a standard indicator (plus its companion stacked indicators) and clear its store state. */
export function removeStandardIndicator(chart: Chart, name: string): void {
  const c = chart as ChartV9;
  const store = useChartStore.getState();
  const pid = store.indicatorPaneMap.get(name);
  if (!pid) return;
  c.removeIndicator(pid, name);
  // When removing VOL, also remove its companion stacked indicators
  // so the vol_pane is fully destroyed and no ghost bars remain.
  if (name === 'VOL') {
    try { c.removeIndicator('vol_pane', 'VOL_LABELS'); } catch { /* ignore */ }
    try { c.removeIndicator('vol_pane', 'CROSS_VOL'); } catch { /* ignore */ }
  }
  if (name === 'RSI') {
    try { c.removeIndicator(pid, 'RSI_ZONES'); } catch { /* ignore */ }
    try { c.removeIndicator(pid, 'RSI_DIVERGENCE'); } catch { /* ignore */ }
  }
  const next = new Map(store.indicatorPaneMap);
  next.delete(name);
  store.setIndicatorPaneMap(next);
  store.clearIndicatorParam(name);
  store.clearIndicatorColors(name);
  void useChartStore.getState().rebalancePanes();
}
